import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MenuPublishBar from "@/components/MenuPublishBar";

// Phase 12 — Quick Availability.
//
// Design goals:
// - < 2s per toggle (optimistic UI + single-purpose PATCH endpoints).
// - No modals, no confirmations, no page reloads.
// - Search + filters + multi-select + mass actions.
// - Draft-only writes: nothing goes to the customer menu until Publish.
//
// Reuses existing endpoints where possible; new endpoints are limited to
// category visibility, department status, and bulk-availability-by-ids.

const TABS = [
  { key: "products", label: "Prodotti" },
  { key: "categories", label: "Categorie" },
  { key: "departments", label: "Reparti" },
];

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "out", label: "Solo esauriti" },
  { key: "in", label: "Solo disponibili" },
  { key: "bar", label: "Solo bar" },
  { key: "kitchen", label: "Solo cucina" },
  { key: "beach", label: "Solo beach" },
];

const norm = (s) => (s || "").toString().toLowerCase();

export default function AdminQuickAvailability() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [p, c, d] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/categories"),
      api.get("/admin/departments"),
    ]);
    setProducts(p.data);
    setCategories(c.data);
    setDepartments(d.data.departments || []);
  };
  useEffect(() => { reload().catch(() => toast.error("Errore nel caricamento dati")); }, []);
  useEffect(() => { setSelected(new Set()); }, [tab, filter, search]);

  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const filteredProducts = useMemo(() => {
    const q = norm(search);
    return products.filter((p) => {
      if (q && !norm(p.name + " " + (catById[p.category_id]?.name || "")).includes(q)) return false;
      if (filter === "out" && p.available !== false) return false;
      if (filter === "in" && p.available === false) return false;
      if (["bar", "kitchen", "beach"].includes(filter) && p.department !== filter) return false;
      return true;
    });
  }, [products, search, filter, catById]);

  const filteredCategories = useMemo(() => {
    const q = norm(search);
    return categories.filter((c) => {
      if (q && !norm(c.name).includes(q)) return false;
      if (filter === "out" && c.visible !== false) return false;
      if (filter === "in" && c.visible === false) return false;
      if (["bar", "kitchen", "beach"].includes(filter) && c.department !== filter) return false;
      return true;
    });
  }, [categories, search, filter]);

  const toggleProduct = async (p, next) => {
    // Optimistic update; snap back on error.
    setProducts((xs) => xs.map((x) => x.id === p.id ? { ...x, available: next } : x));
    try {
      await api.patch(`/admin/products/${p.id}/availability?available=${next}`);
    } catch {
      setProducts((xs) => xs.map((x) => x.id === p.id ? { ...x, available: !next } : x));
      toast.error("Errore aggiornamento");
    }
  };

  const toggleCategory = async (c, next) => {
    setCategories((xs) => xs.map((x) => x.id === c.id ? { ...x, visible: next } : x));
    try {
      await api.patch(`/admin/categories/${c.id}/visibility`, { visible: next });
    } catch {
      setCategories((xs) => xs.map((x) => x.id === c.id ? { ...x, visible: !next } : x));
      toast.error("Errore aggiornamento");
    }
  };

  const toggleDepartment = async (d, next) => {
    setDepartments((xs) => xs.map((x) => x.name === d.name ? { ...x, enabled: next } : x));
    try {
      await api.patch(`/admin/departments/${d.name}/status`, { enabled: next });
    } catch {
      setDepartments((xs) => xs.map((x) => x.name === d.name ? { ...x, enabled: !next } : x));
      toast.error("Errore aggiornamento");
    }
  };

  // ------- multi-select ---------
  const toggleSel = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const bulkAvailability = async (available) => {
    if (selected.size === 0) return;
    setBusy(true);
    const ids = Array.from(selected);
    setProducts((xs) => xs.map((x) => selected.has(x.id) ? { ...x, available } : x));
    try {
      await api.post("/admin/products/bulk-availability-ids", { product_ids: ids, available });
      toast.success(`${ids.length} prodotti aggiornati`);
      setSelected(new Set());
    } catch {
      toast.error("Errore aggiornamento massivo");
      await reload();
    } finally { setBusy(false); }
  };
  const bulkVisibility = async (visible) => {
    if (selected.size === 0) return;
    setBusy(true);
    const ids = Array.from(selected);
    setCategories((xs) => xs.map((x) => selected.has(x.id) ? { ...x, visible } : x));
    try {
      await api.post("/admin/categories/visibility/bulk", { category_ids: ids, visible });
      toast.success(`${ids.length} categorie aggiornate`);
      setSelected(new Set());
    } catch {
      toast.error("Errore aggiornamento massivo");
      await reload();
    } finally { setBusy(false); }
  };

  // ------- mass actions (no selection needed) ---------
  const massAllProducts = async (available) => {
    setBusy(true);
    setProducts((xs) => xs.map((x) => ({ ...x, available })));
    try {
      const active_ids = products.map((p) => p.id);
      await api.post("/admin/products/bulk-availability-ids", { product_ids: active_ids, available });
      toast.success(`Tutti i prodotti → ${available ? "disponibili" : "esauriti"}`);
    } catch { toast.error("Errore azione massiva"); await reload(); }
    finally { setBusy(false); }
  };
  const massAllCategories = async (visible) => {
    setBusy(true);
    setCategories((xs) => xs.map((x) => ({ ...x, visible })));
    try {
      await api.post("/admin/categories/visibility/bulk", { category_ids: null, visible });
      toast.success(`Tutte le categorie → ${visible ? "attive" : "nascoste"}`);
    } catch { toast.error("Errore azione massiva"); await reload(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="quick-availability-page">
      <MenuPublishBar />

      <div className="flex flex-wrap gap-2 items-center">
        <h1 className="text-2xl font-serif mr-4">Disponibilità Rapida</h1>
        {TABS.map((t) => (
          <button key={t.key}
                  data-testid={`qa-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${tab === t.key ? "border-bronze-600 bg-bronze-600 text-white" : "border-sand-300 bg-white hover:bg-sand-100"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          data-testid="qa-search"
          placeholder="Cerca…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key}
                    data-testid={`qa-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`text-xs rounded-full px-3 py-1 border ${filter === f.key ? "border-bronze-600 bg-bronze-50 text-bronze-800" : "border-sand-300 bg-white hover:bg-sand-100"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mass actions */}
      {tab === "products" && (
        <div className="flex flex-wrap gap-2" data-testid="qa-mass-actions-products">
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => massAllProducts(true)}
                  data-testid="qa-mass-all-available">Segna tutto disponibile</Button>
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => massAllProducts(false)}
                  data-testid="qa-mass-all-out">Segna tutto esaurito</Button>
        </div>
      )}
      {tab === "categories" && (
        <div className="flex flex-wrap gap-2" data-testid="qa-mass-actions-categories">
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => massAllCategories(false)}
                  data-testid="qa-mass-all-hidden">Nascondi tutte le categorie</Button>
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => massAllCategories(true)}
                  data-testid="qa-mass-all-visible">Ripristina categorie</Button>
        </div>
      )}

      {/* PRODUCTS TAB */}
      {tab === "products" && (
        <div className="rounded-xl border border-sand-300 bg-white overflow-hidden" data-testid="qa-products-list">
          {filteredProducts.map((p) => {
            const out = p.available === false;
            const isSel = selected.has(p.id);
            return (
              <div key={p.id} data-testid={`qa-product-row-${p.id}`}
                   className={`flex items-center gap-3 px-4 py-2.5 border-b border-sand-100 last:border-b-0 ${isSel ? "bg-bronze-50/40" : ""}`}>
                <input type="checkbox" checked={isSel}
                       onChange={() => toggleSel(p.id)}
                       data-testid={`qa-product-check-${p.id}`}
                       className="w-4 h-4" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-ink-500 truncate">
                    {catById[p.category_id]?.name || "—"} · {p.department || "—"}
                  </div>
                </div>
                <button data-testid={`qa-product-toggle-${p.id}`}
                        onClick={() => toggleProduct(p, out)}
                        className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${out ? "border-red-300 text-red-700 bg-red-50 hover:bg-red-100" : "border-green-300 text-green-700 bg-green-50 hover:bg-green-100"}`}>
                  {out ? "🔴 Esaurito" : "🟢 Disponibile"}
                </button>
              </div>
            );
          })}
          {filteredProducts.length === 0 && <div className="px-4 py-8 text-center text-ink-500 text-sm">Nessun prodotto corrispondente ai filtri.</div>}
        </div>
      )}

      {/* CATEGORIES TAB */}
      {tab === "categories" && (
        <div className="rounded-xl border border-sand-300 bg-white overflow-hidden" data-testid="qa-categories-list">
          {filteredCategories.map((c) => {
            const hidden = c.visible === false;
            const isSel = selected.has(c.id);
            return (
              <div key={c.id} data-testid={`qa-category-row-${c.id}`}
                   className={`flex items-center gap-3 px-4 py-2.5 border-b border-sand-100 last:border-b-0 ${isSel ? "bg-bronze-50/40" : ""}`}>
                <input type="checkbox" checked={isSel}
                       onChange={() => toggleSel(c.id)}
                       data-testid={`qa-category-check-${c.id}`}
                       className="w-4 h-4" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-ink-500 truncate">{c.department || "—"}{c.parent_id ? " · sub" : ""}</div>
                </div>
                <button data-testid={`qa-category-toggle-${c.id}`}
                        onClick={() => toggleCategory(c, hidden)}
                        className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${hidden ? "border-red-300 text-red-700 bg-red-50 hover:bg-red-100" : "border-green-300 text-green-700 bg-green-50 hover:bg-green-100"}`}>
                  {hidden ? "🔴 Nascosta" : "🟢 Attiva"}
                </button>
              </div>
            );
          })}
          {filteredCategories.length === 0 && <div className="px-4 py-8 text-center text-ink-500 text-sm">Nessuna categoria.</div>}
        </div>
      )}

      {/* DEPARTMENTS TAB */}
      {tab === "departments" && (
        <div className="rounded-xl border border-sand-300 bg-white overflow-hidden" data-testid="qa-departments-list">
          {departments.map((d) => (
            <div key={d.name} data-testid={`qa-dept-row-${d.name}`}
                 className="flex items-center gap-3 px-4 py-3 border-b border-sand-100 last:border-b-0">
              <div className="flex-1">
                <div className="font-medium capitalize">{d.name}</div>
                <div className="text-xs text-ink-500">{d.product_count} prodotti</div>
              </div>
              <button data-testid={`qa-dept-toggle-${d.name}`}
                      onClick={() => toggleDepartment(d, !d.enabled)}
                      className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium border transition-colors ${!d.enabled ? "border-red-300 text-red-700 bg-red-50 hover:bg-red-100" : "border-green-300 text-green-700 bg-green-50 hover:bg-green-100"}`}>
                {!d.enabled ? "🔴 Spento" : "🟢 Acceso"}
              </button>
            </div>
          ))}
          {departments.length === 0 && <div className="px-4 py-8 text-center text-ink-500 text-sm">Nessun reparto configurato.</div>}
          <div className="px-4 py-3 bg-sand-50 text-xs text-ink-500">
            Spegnendo un reparto tutti i prodotti che vi appartengono diventano "Esauriti" sul menu cliente (dopo la Pubblicazione). Nessun dato viene cancellato.
          </div>
        </div>
      )}

      {/* Sticky bulk action bar */}
      {selected.size > 0 && (tab === "products" || tab === "categories") && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 rounded-full bg-white border border-sand-300 shadow-lg px-5 py-2.5 flex items-center gap-3" data-testid="qa-bulk-bar">
          <span className="text-sm font-medium">{selected.size} selezionati</span>
          {tab === "products" ? (
            <>
              <Button size="sm" disabled={busy} onClick={() => bulkAvailability(false)}
                      data-testid="qa-bulk-out" className="rounded-full">Segna esauriti</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => bulkAvailability(true)}
                      data-testid="qa-bulk-in" className="rounded-full">Ripristina</Button>
            </>
          ) : (
            <>
              <Button size="sm" disabled={busy} onClick={() => bulkVisibility(false)}
                      data-testid="qa-bulk-hidden" className="rounded-full">Nascondi</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => bulkVisibility(true)}
                      data-testid="qa-bulk-visible" className="rounded-full">Attiva</Button>
            </>
          )}
          <button onClick={() => setSelected(new Set())}
                  data-testid="qa-bulk-clear"
                  className="text-xs text-ink-500 hover:underline">Annulla</button>
        </div>
      )}
    </div>
  );
}
